import React, { useState, useEffect, useCallback } from 'react';
import {
    Package, Plus, Search, LayoutGrid, List, Filter, AlertTriangle,
    Loader2, Tag, TrendingUp, TrendingDown, DollarSign, Archive,
    Download, Upload, FolderOpen, RefreshCw, X, ChevronDown
} from 'lucide-react';
import {
    fetchProducts, fetchCategories, fetchSuppliers, getInventoryStats,
    createProduct, updateProduct, deleteProduct, adjustStock,
    createCategory, updateCategory, deleteCategory, exportProducts, importProducts
} from '../api/inventory';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import StockAdjustModal from './StockAdjustModal';
import CategoryModal from './CategoryModal';

const STOCK_FILTERS = [
    { value: 'all', label: 'All Products' },
    { value: 'in_stock', label: 'In Stock' },
    { value: 'low_stock', label: 'Low Stock' },
    { value: 'out_of_stock', label: 'Out of Stock' },
];

const InventoryPage = ({ userEmail }) => {
    // Data state
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [stats, setStats] = useState({
        total_products: 0,
        total_value: 0,
        low_stock_count: 0,
        out_of_stock_count: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI state
    const [viewMode, setViewMode] = useState('grid');
    const [searchTerm, setSearchTerm] = useState('');
    const [stockFilter, setStockFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);

    // Modal state
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [stockAdjustProduct, setStockAdjustProduct] = useState(null);
    const [stockAdjustType, setStockAdjustType] = useState('add');
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [productsRes, categoriesRes, suppliersRes, statsRes] = await Promise.all([
                fetchProducts({
                    search: searchTerm,
                    category: categoryFilter,
                    stockStatus: stockFilter !== 'all' ? stockFilter : undefined,
                    sortBy,
                    sortOrder,
                }).catch((err) => {
                    console.warn('[Inventory] Products API not available:', err.message);
                    return { data: [] };
                }),
                fetchCategories().catch(() => ({ data: [] })),
                fetchSuppliers().catch(() => ({ data: [] })),
                getInventoryStats().catch(() => ({
                    data: { total_products: 0, total_value: 0, low_stock_count: 0, out_of_stock_count: 0 }
                })),
            ]);

            setProducts(productsRes.data || []);
            setCategories(categoriesRes.data || []);
            setSuppliers(suppliersRes.data || []);
            setStats(statsRes.data || {});
        } catch (err) {
            console.error('Failed to load inventory:', err);
            setError(err.message || 'Failed to load inventory');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, categoryFilter, stockFilter, sortBy, sortOrder]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter products locally for instant feedback
    const filteredProducts = products.filter(product => {
        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const matchesSearch =
                product.name?.toLowerCase().includes(term) ||
                product.sku?.toLowerCase().includes(term) ||
                product.barcode?.toLowerCase().includes(term);
            if (!matchesSearch) return false;
        }

        // Category filter
        if (categoryFilter && product.category_id !== categoryFilter) {
            return false;
        }

        // Stock filter
        const qty = product.quantity || 0;
        const minLevel = product.min_stock_level || 5;
        if (stockFilter === 'in_stock' && qty <= minLevel) return false;
        if (stockFilter === 'low_stock' && (qty === 0 || qty > minLevel)) return false;
        if (stockFilter === 'out_of_stock' && qty > 0) return false;

        return true;
    });

    // Sort products
    const sortedProducts = [...filteredProducts].sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
            case 'name':
                aVal = a.name?.toLowerCase() || '';
                bVal = b.name?.toLowerCase() || '';
                break;
            case 'price':
                aVal = a.selling_price || 0;
                bVal = b.selling_price || 0;
                break;
            case 'quantity':
                aVal = a.quantity || 0;
                bVal = b.quantity || 0;
                break;
            case 'created':
                aVal = new Date(a.created_at || 0);
                bVal = new Date(b.created_at || 0);
                break;
            default:
                return 0;
        }
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
    });

    // Handlers
    const handleCreateProduct = () => {
        setEditingProduct(null);
        setIsProductModalOpen(true);
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setIsProductModalOpen(true);
    };

    const handleSaveProduct = async (productData) => {
        setIsSaving(true);
        try {
            if (editingProduct) {
                const res = await updateProduct(editingProduct.id, productData);
                setProducts(prev => prev.map(p => p.id === editingProduct.id ? res.data : p));
            } else {
                const res = await createProduct(productData);
                setProducts(prev => [res.data, ...prev]);
            }
            setIsProductModalOpen(false);
            setEditingProduct(null);
            loadData(); // Refresh stats
        } catch (err) {
            console.error('Failed to save product:', err);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteProduct = async (productId) => {
        try {
            await deleteProduct(productId);
            setProducts(prev => prev.filter(p => p.id !== productId));
            loadData(); // Refresh stats
        } catch (err) {
            console.error('Failed to delete product:', err);
            alert('Failed to delete product: ' + err.message);
        }
    };

    const handleAdjustStock = (product, type) => {
        setStockAdjustProduct(product);
        setStockAdjustType(type);
    };

    const handleSaveStockAdjust = async (adjustment) => {
        setIsSaving(true);
        try {
            const res = await adjustStock(stockAdjustProduct.id, adjustment);
            setProducts(prev => prev.map(p => p.id === stockAdjustProduct.id ? res.data : p));
            setStockAdjustProduct(null);
            loadData(); // Refresh stats
        } catch (err) {
            console.error('Failed to adjust stock:', err);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateCategory = async (categoryData) => {
        setIsSaving(true);
        try {
            const res = await createCategory(categoryData);
            setCategories(prev => [...prev, res.data]);
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateCategory = async (categoryId, categoryData) => {
        setIsSaving(true);
        try {
            const res = await updateCategory(categoryId, categoryData);
            setCategories(prev => prev.map(c => c.id === categoryId ? res.data : c));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        try {
            await deleteCategory(categoryId);
            setCategories(prev => prev.filter(c => c.id !== categoryId));
        } catch (err) {
            console.error('Failed to delete category:', err);
            throw err;
        }
    };

    const handleExport = async () => {
        try {
            const res = await exportProducts({ category: categoryFilter, stockStatus: stockFilter });
            // Trigger download
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `inventory_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
        } catch (err) {
            console.error('Export failed:', err);
            alert('Failed to export: ' + err.message);
        }
    };

    const getCategoryName = (categoryId) => {
        return categories.find(c => c.id === categoryId)?.name || '';
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0);
    };

    return (
        <div className="flex flex-col h-full bg-[#050810]">
            {/* Hero */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(255, 77, 77, 0.95), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Inventory Management</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">Track your products</h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Manage stock levels, track inventory value, and get low-stock alerts.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setIsCategoryModalOpen(true)}
                                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium flex items-center gap-2 transition"
                            >
                                <FolderOpen size={18} />
                                Categories
                            </button>
                            <button
                                onClick={handleCreateProduct}
                                className="px-4 py-2.5 bg-white text-[#ff4d4d] hover:bg-white/90 rounded-xl font-semibold flex items-center gap-2 transition"
                            >
                                <Plus size={18} />
                                Add Product
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6 pb-6 -mt-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        {
                            label: 'Total Products',
                            value: stats.total_products || products.length,
                            icon: Package,
                            color: 'text-[#ff4d4d]',
                            bg: 'bg-[#ff4d4d]/10'
                        },
                        {
                            label: 'Inventory Value',
                            value: formatCurrency(stats.total_value),
                            icon: DollarSign,
                            color: 'text-emerald-500',
                            bg: 'bg-emerald-500/10'
                        },
                        {
                            label: 'Low Stock',
                            value: stats.low_stock_count,
                            icon: AlertTriangle,
                            color: 'text-amber-500',
                            bg: 'bg-amber-500/10',
                            onClick: () => setStockFilter('low_stock')
                        },
                        {
                            label: 'Out of Stock',
                            value: stats.out_of_stock_count,
                            icon: Archive,
                            color: 'text-red-500',
                            bg: 'bg-red-500/10',
                            onClick: () => setStockFilter('out_of_stock')
                        },
                    ].map((stat, i) => (
                        <div
                            key={stat.label}
                            onClick={stat.onClick}
                            className={`p-5 bg-[#0d1424]/80 backdrop-blur border border-white/10 rounded-2xl shadow-xl transition-all animate-fadeIn ${
                                stat.onClick ? 'cursor-pointer hover:border-white/20' : ''
                            }`}
                            style={{ animationDelay: `${i * 50}ms` }}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${stat.bg}`}>
                                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-white/40 uppercase tracking-wider">{stat.label}</p>
                                    <p className={`text-2xl font-bold ${stat.color}`}>
                                        {loading ? '-' : stat.value}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters Bar */}
                <div className="p-4 bg-[#0d1424]/80 backdrop-blur border border-white/10 rounded-2xl shadow-xl mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px] max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 focus:border-[#ff4d4d] transition"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        {/* Stock Filter */}
                        <div className="relative">
                            <select
                                value={stockFilter}
                                onChange={(e) => setStockFilter(e.target.value)}
                                className="pl-4 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none"
                            >
                                {STOCK_FILTERS.map(f => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                        </div>

                        {/* Category Filter */}
                        {categories.length > 0 && (
                            <div className="relative">
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="pl-4 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 appearance-none"
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                            </div>
                        )}

                        <div className="flex-1" />

                        {/* View Toggle */}
                        <div className="flex bg-white/5 p-1 rounded-lg border border-white/10">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition ${
                                    viewMode === 'grid'
                                        ? 'bg-white/10 text-[#ff4d4d]'
                                        : 'text-white/40 hover:text-white'
                                }`}
                            >
                                <LayoutGrid size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition ${
                                    viewMode === 'list'
                                        ? 'bg-white/10 text-[#ff4d4d]'
                                        : 'text-white/40 hover:text-white'
                                }`}
                            >
                                <List size={18} />
                            </button>
                        </div>

                        {/* Refresh */}
                        <button
                            onClick={loadData}
                            disabled={loading}
                            className="p-2.5 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition"
                            title="Refresh"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>

                        {/* Export */}
                        <button
                            onClick={handleExport}
                            className="p-2.5 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition"
                            title="Export CSV"
                        >
                            <Download size={18} />
                        </button>
                    </div>

                    {/* Active Filters */}
                    {(stockFilter !== 'all' || categoryFilter || searchTerm) && (
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                            <span className="text-sm text-white/40">Filters:</span>
                            {stockFilter !== 'all' && (
                                <span className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded-full text-xs font-medium flex items-center gap-1">
                                    {STOCK_FILTERS.find(f => f.value === stockFilter)?.label}
                                    <button onClick={() => setStockFilter('all')}><X size={12} /></button>
                                </span>
                            )}
                            {categoryFilter && (
                                <span className="px-2 py-1 bg-[#ff4d4d]/10 text-[#ff4d4d] rounded-full text-xs font-medium flex items-center gap-1">
                                    {getCategoryName(categoryFilter)}
                                    <button onClick={() => setCategoryFilter('')}><X size={12} /></button>
                                </span>
                            )}
                            {searchTerm && (
                                <span className="px-2 py-1 bg-sky-500/10 text-sky-500 rounded-full text-xs font-medium flex items-center gap-1">
                                    "{searchTerm}"
                                    <button onClick={() => setSearchTerm('')}><X size={12} /></button>
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    setStockFilter('all');
                                    setCategoryFilter('');
                                    setSearchTerm('');
                                }}
                                className="text-xs text-white/40 hover:text-white ml-2"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 size={32} className="text-[#ff4d4d] animate-spin mb-3" />
                        <p className="text-sm text-white/50">Loading inventory...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-4 rounded-full bg-red-500/10 mb-4">
                            <AlertTriangle size={32} className="text-red-500" />
                        </div>
                        <p className="text-sm text-red-500 mb-4">{error}</p>
                        <button
                            onClick={loadData}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition"
                        >
                            Try Again
                        </button>
                    </div>
                ) : sortedProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-6 rounded-2xl bg-white/5 mb-4">
                            <Package size={48} className="text-white/30" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                            {searchTerm || stockFilter !== 'all' || categoryFilter ? 'No matching products' : 'No products yet'}
                        </h3>
                        <p className="text-sm text-white/50 mb-6 text-center max-w-md">
                            {searchTerm || stockFilter !== 'all' || categoryFilter
                                ? 'Try adjusting your filters or search term.'
                                : 'Add your first product to start tracking inventory.'}
                        </p>
                        {!searchTerm && stockFilter === 'all' && !categoryFilter && (
                            <button
                                onClick={handleCreateProduct}
                                className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition"
                            >
                                <Plus size={18} />
                                Add Product
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={viewMode === 'grid'
                        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                        : 'flex flex-col gap-3'
                    }>
                        {sortedProducts.map((product, index) => (
                            <div
                                key={product.id}
                                className="animate-fadeIn"
                                style={{ animationDelay: `${index * 30}ms` }}
                            >
                                <ProductCard
                                    product={product}
                                    viewMode={viewMode}
                                    categoryName={getCategoryName(product.category_id)}
                                    onEdit={() => handleEditProduct(product)}
                                    onDelete={() => handleDeleteProduct(product.id)}
                                    onAdjustStock={(type) => handleAdjustStock(product, type)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {isProductModalOpen && (
                <ProductModal
                    product={editingProduct}
                    categories={categories}
                    suppliers={suppliers}
                    onSave={handleSaveProduct}
                    onClose={() => {
                        setIsProductModalOpen(false);
                        setEditingProduct(null);
                    }}
                    isSaving={isSaving}
                />
            )}

            {/* Stock Adjust Modal */}
            {stockAdjustProduct && (
                <StockAdjustModal
                    product={stockAdjustProduct}
                    type={stockAdjustType}
                    onSave={handleSaveStockAdjust}
                    onClose={() => setStockAdjustProduct(null)}
                    isSaving={isSaving}
                />
            )}

            {/* Category Modal */}
            {isCategoryModalOpen && (
                <CategoryModal
                    categories={categories}
                    onCreateCategory={handleCreateCategory}
                    onUpdateCategory={handleUpdateCategory}
                    onDeleteCategory={handleDeleteCategory}
                    onClose={() => setIsCategoryModalOpen(false)}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default InventoryPage;
